import React from 'react'

import { Icon, Typography } from '~components/internal'

import './WizardStepper.scss'

export interface WizardStepperStep {
  label: string
  key: string
}

export interface WizardStepperProps {
  steps: WizardStepperStep[]
  currentStep: number
  onStepClick?: (stepIndex: number) => void
  /** Allow clicking ahead to steps that have not been reached yet. */
  allowStepSkipping?: boolean
}

const WizardStepper: React.FC<WizardStepperProps> = ({
  steps,
  currentStep,
  onStepClick,
  allowStepSkipping = false,
}) => {
  return (
    <nav className='wizard-stepper' aria-label='Progress'>
      {steps.map((step, index) => {
        const isCompleted = index < currentStep
        const isCurrent = index === currentStep
        const isClickable = onStepClick && (allowStepSkipping || index <= currentStep)

        return (
          <React.Fragment key={step.key}>
            <div
              className={`wizard-stepper__step${isClickable ? ' wizard-stepper__step--clickable' : ''}`}
              onClick={() => isClickable && onStepClick?.(index)}
              role='button'
              tabIndex={isClickable ? 0 : -1}
              aria-current={isCurrent ? 'step' : undefined}
              aria-label={`Step ${index + 1}: ${step.label}${isCompleted ? ' (completed)' : isCurrent ? ' (current)' : ''}`}
              onKeyDown={(e) => {
                if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault()
                  onStepClick?.(index)
                }
              }}
            >
              {isCompleted ? (
                <div className='wizard-stepper__circle wizard-stepper__circle--completed'>
                  <Icon name='check' fontSize='inherit' />
                </div>
              ) : (
                <div
                  className={`wizard-stepper__circle ${isCurrent ? 'wizard-stepper__circle--current' : 'wizard-stepper__circle--upcoming'}`}
                >
                  <Typography variant='caption' color='inherit'>
                    {index + 1}
                  </Typography>
                </div>
              )}
              <Typography
                className={`wizard-stepper__label${isCurrent ? ' wizard-stepper__label--current' : ''}`}
                variant='body2'
                color={isCurrent ? 'textPrimary' : isCompleted ? 'primary.main' : 'textSecondary'}
              >
                {step.label}
              </Typography>
            </div>

            {index < steps.length - 1 && (
              <div
                className={`wizard-stepper__connector ${index < currentStep ? 'wizard-stepper__connector--completed' : 'wizard-stepper__connector--upcoming'}`}
              />
            )}
          </React.Fragment>
        )
      })}
    </nav>
  )
}

export default WizardStepper
